/* eslint-disable @atlaskit/platform/no-preconditioning -- Existing chat-store actions gate feature behavior from runtime flags. */

import { flushSync } from 'react-dom';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import type { ChatEntryPoint } from '@atlaskit/rovo-triggers/chat-entry-point';
import type {
	RovoApp,
	SourceMode,
} from '@atlassian/conversation-assistant-chat-prompt-input/controllers/chat-mode-store/types';
import {
	type FollowUpSource,
	type InteractionSource,
} from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import {
	type AutoActionApprovalMode,
	type ChatMessage,
	type Dialogues,
	isAssistantMessage,
	isHumanMessage,
	type StreamResponse,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { generateConversationName } from '@atlassian/conversation-assistant-service/services/assistance-service/utils/conversation';
import {
	type AgentVersion,
	CHAT_STORE_STATUS,
	type ConversationResultAttributionContext,
	StoreActionLoggableName,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { createSafeAction, type SafeAction } from './createSafeAction';
import { generateId } from './generateId';
import { initializeConversationData } from './initializeConversationData';
import { processConversationMessages } from './processConversationMessages';
import { resolveConversationSeedAgent } from './resolveConversationSeedAgent';
import { setSelectedConversation } from './setSelectedConversation';
import type { ChatStoreAction } from './types';

type CreateSeededConversationOptions = {
	name?: string | null;
	dialogues: Dialogues[];
	sourceId?: string;
	rovoJourneyId?: string;
	id?: string;
	followUpSource: Omit<FollowUpSource, 'none'>;
	agentId?: string;
	agentVersion?: AgentVersion;
	resultAttributionContext?: ConversationResultAttributionContext;
	/** Product surface that launched this conversation, used for analytics attribution. */
	chatEntryPoint?: ChatEntryPoint;
	tags?: string[];
	skipNextFetch?: boolean;
	/**
	 * When false, create the chat but leave the currently selected chat unchanged.
	 * Defaults to true so existing callers keep today's behavior.
	 */
	selectConversation?: boolean;
	/**
	 * Fires after the conversation is created and before it is selected, so state keyed to the
	 * conversation id can be updated before `currentConversationId` changes.
	 */
	onConversationCreated?: (conversationId: string) => void;
	// When the seeded conversation is scoped to a Rovo Space (e.g. created from
	// the SpaceLandingPage chat input or a space starter), set this so the
	// conversation is associated with the space immediately on optimistic creation.
	rovoSpaceId?: string;
	mode?: {
		autoActionApprovalMode?: AutoActionApprovalMode;
		deepResearchEnabled?: boolean;
		thinkDeeperEnabled?: boolean;
		fastModeEnabled?: boolean;
		taskModeEnabled?: boolean;
		webSearchEnabled?: boolean;
		sourceMode?: SourceMode;
		useCurrentPageContext?: boolean;
		spaceSourceCount?: number;
		appFilters?: RovoApp[];
		selectedCloudIds?: string[];
	};
};

export const createSeededConversation: SafeAction<
	[CreateSeededConversationOptions],
	string | undefined
> = createSafeAction(
	StoreActionLoggableName.createSeededConversation,
	(options: CreateSeededConversationOptions): ChatStoreAction<Promise<string | undefined>> =>
		async (
			{ dispatch },
			{
				assistanceService,
				experienceTracker,
				sendAnalyticsEvent,
				setMessages,
				getChatInitiator,
				onNewConversationStartingWithSendMessage,
			},
		) => {
			experienceTracker.start({
				name: ExperienceName.CREATE_SEEDED_CONVERSATION_CHANNEL,
				id: ExperienceName.CREATE_SEEDED_CONVERSATION_CHANNEL,
			});

			const createConversationOptions =
				options.rovoSpaceId &&
				FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false)
					? { ...options, name: null }
					: options;
			const optimisticConversationName = options.name ?? generateConversationName();

			const createdConversation =
				await assistanceService.createConversationChannel(createConversationOptions);
			const humanMessage = createdConversation.messages?.find((message) => isHumanMessage(message));
			const assistantMessage = createdConversation.messages?.find((message) =>
				isAssistantMessage(message),
			);
			const humanMessageId = humanMessage ? humanMessage.id : '';
			const assistantMessageId = assistantMessage ? assistantMessage.id : '';
			const conversationTags = createdConversation.tags ?? options.tags;

			// The header reads the displayed agent only from `conversation.agent.agent_id`,
			// so the chat-new/seeded flow must seed it from the known agent id (preferring a
			// backend-returned agent). Without this, a seeded conversation opened with a
			// specific agent shows default Rovo.
			const seededConversationAgent = resolveConversationSeedAgent({
				backendAgent: createdConversation.agent,
				agentId: options.agentId,
				legacyAgent: createdConversation.agent,
			});
			const seededConversationAgentFields = seededConversationAgent
				? { agent: seededConversationAgent }
				: {};

			dispatch(
				initializeConversationData({
					conversationId: createdConversation.id,
					data: {
						id: createdConversation.id,
						name: createdConversation.name?.trim() || optimisticConversationName,
						timeCreated: createdConversation.time_created,
						status: CHAT_STORE_STATUS.idle,
						traces: [],
						sourceId: options.sourceId ?? undefined,
						// Front-end-only journey attribution; the service serializer ignores this field.
						rovoJourneyId: options.rovoJourneyId,
						sourceAgentId: options.agentId,
						agentVersion: options.agentVersion,
						resultAttributionContext: options.resultAttributionContext,
						// Set explicitly (including `undefined`) rather than conditionally spread:
						// `initializeConversationData` shallow-merges over any existing slot, so
						// omitting the key would let a stale entry point from a previous launch
						// leak onto this conversation and mis-attribute it.
						chatEntryPoint: options.chatEntryPoint,
						// Same reasoning: a stale 3P cohort would otherwise be mirrored onto this
						// conversation's events, including AI-MAU events the experiment does not own.
						thirdPartyPrompt: undefined,
						messages: [],
						...seededConversationAgentFields,
						...(conversationTags !== undefined ? { tags: conversationTags } : {}),
						...(options.skipNextFetch ? { skipNextFetch: true } : {}),
						// Scope the seeded conversation to a Rovo Space when applicable so the
						// nav and landing page can immediately render it under the correct space.
						...(options.rovoSpaceId
							? FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false)
								? { rovoSpaceId: options.rovoSpaceId }
								: {}
							: {}),
					},
				}),
			);

			const chatInitiator = getChatInitiator();

			// Build synthetic ChatMessage[] from the seeded dialogues and pass directly to
			// processConversationMessages, which handles all boundary content extraction
			// (lumina, chart, expand, sculptor) consistently with fetchConversation —
			// without an extra getChat network call.
			const syntheticMessages: ChatMessage[] = options.dialogues.flatMap((dialogue) => {
				sendAnalyticsEvent({
					eventType: 'track',
					action: 'sent',
					actionSubject: 'message',
					attributes: {
						messageId: humanMessageId,
						interactionSource: 'movedOver' as InteractionSource,
						followUpSource: options.followUpSource,
						timestamp: new Date().toISOString(),
						initiator: chatInitiator,
						conversationChannelId: createdConversation.id,
						// Explicitly override a stale context default while PubSub waits for this
						// seeded conversation to finish creating before updating the shared ref.
						...(options.rovoJourneyId ? { rovoJourneyId: options.rovoJourneyId } : {}),
					},
				});
				sendAnalyticsEvent({
					eventType: 'track',
					action: 'received',
					actionSubject: 'agentMessage',
					attributes: {
						messageId: assistantMessageId,
						humanMessageId,
						pluginsInvoked: [],
						hasSources: false,
						timestamp: new Date().toISOString(),
						initiator: chatInitiator,
						conversationChannelId: createdConversation.id,
					},
				});

				// When mimeType is 'text/adf', parse the ADF JSON string into a DocNode
				// so encodeAdf's isAdfContent check can pass it through natively.
				let agentContent: string | DocNode = dialogue.agent_message.content;
				if (dialogue.agent_message.mimeType === 'text/adf') {
					try {
						agentContent = JSON.parse(dialogue.agent_message.content);
					} catch {
						// If parsing fails, fall back to raw string content
					}
				}

				// Use raw ChatMessage types (not createStoreHuman/createStoreAssistant) so that
				// processConversationMessages can apply all boundary content extraction
				// (lumina, chart, expand, sculptor) before producing the final StoreChatItem[].
				const humanMessage: ChatMessage = {
					role: 'HUMAN',
					id: generateId(),
					content: dialogue.human_message.content,
					time_created: new Date().toISOString(),
					user_ari: 'unknown',
					plugin_invocations: [],
					isFromSeededConversation: true,
				};
				const assistantStreamResponse: StreamResponse = {
					type: 'FINAL_RESPONSE',
					message: {
						message: {
							role: 'ASSISTANT',
							id: generateId(),
							conversation_channel_id: createdConversation.id,
							content: agentContent,
							time_created: new Date().toISOString(),
							user_ari: 'unknown',
							plugin_invocations: [],
							author: {
								named_id: 'ai_mate_agent',
								name: 'unknown',
								actor_type: 'AGENT',
								ari: null,
								id: 'unknown',
							},
						},
					},
				};

				return [humanMessage, assistantStreamResponse];
			});

			const processedMessages = processConversationMessages({
				latestMessages: syntheticMessages,
				promptContextViewSource: 'original',
				conversationId: createdConversation.id,
				sendAnalyticsEvent,
			});

			setMessages(createdConversation.id, processedMessages);

			if (options.mode && onNewConversationStartingWithSendMessage) {
				// Initialize per-conversation mode before selection so the first render
				// cannot briefly fall back to default controls and context.
				onNewConversationStartingWithSendMessage(createdConversation.id, options.mode);
			}

			// Commit the callback's React state updates before the selection below is dispatched;
			// otherwise the store update can reach subscribers first.
			flushSync(() => {
				options.onConversationCreated?.(createdConversation.id);
			});

			if (options.selectConversation !== false) {
				dispatch(
					setSelectedConversation({
						conversationId: createdConversation.id,
					}),
				);
			}

			experienceTracker.succeed({
				name: ExperienceName.CREATE_SEEDED_CONVERSATION_CHANNEL,
			});

			return createdConversation.id;
		},
	{
		experienceName: () => ExperienceName.CREATE_SEEDED_CONVERSATION_CHANNEL,
	},
);
